export interface IClauseChecklistStats {
    id: number;
    name: string;
    count: number;
}

export interface IClauseStats {
    id: number;
    name: string;
    down_votes: number;
}

export interface IScopeAccuracyStats {
    date: Date;
    accuracy: number;
}

export interface IAdminAnalyticsEmbed {
    dashboard_url: string | null;
}

export interface IAdminStats {
    scopes: number;
    clause_checklists: IClauseChecklistStats[];
    users: number;
    clauses: IClauseStats[];
    scope_accuracy: IScopeAccuracyStats[];
}

import type { IOrganizationMinimal } from "../organizations/organizations.types";
import type { IUserMinimal } from "../users/users.types";

export interface IAdminOrganizationContextResponse {
    has_organization_context: boolean;
}

// --- Job Run Types ---

export enum JobStatus {
    PENDING = "pending",
    PROCESSING = "processing",
    COMPLETED = "completed",
    PARTIAL_SUCCESS = "partial_success",
    FAILED = "failed",
    CANCELLED = "cancelled",
}

export enum JobTaskStatus {
    QUEUED = "queued",
    PROCESSING = "processing",
    COMPLETED = "completed",
    FAILED = "failed",
    RETRYABLE = "retryable",
    CANCELLED = "cancelled",
}

export enum JobTaskType {
    CLAUSE_ANALYSIS_BATCH = "clause_analysis_batch",
    FIELD_EXTRACTION = "field_extraction",
    FORM_EXTRACTION = "form_extraction",
    TOPIC_SUMMARY = "topic_summary",
    SCOPE_HEADLINE = "scope_headline",
}

export interface IJobClauseRef {
    id: number;
    title: string;
    section_type_names: string[];
}

export interface IFieldPropertyMinimal {
    name: string;
    datatype: string;
}

export interface IFieldDefinitionMinimal {
    id: number;
    name: string;
    properties: IFieldPropertyMinimal[];
}

export interface IJobTaskMetadata {
    scope_id?: number | null;
    clause_sections?:
        | { clause_id: number; section_type_ids: (number | null)[] }[]
        | null;
    batch_size?: number | null;
    fieldset_id?: number | null;
    field_definition_id?: number | null;
    field_definition_ids?: number[] | null;
    form_types?: string[] | null;
    scope_topic_id?: number | null;
    topic_name?: string | null;
    // Enrichment fields (populated by API, not stored in DB)
    clauses?: IJobClauseRef[] | null;
    field_definition?: IFieldDefinitionMinimal | null;
    field_definitions?: IFieldDefinitionMinimal[] | null;
    topic_id?: number | null;
}

export interface IJobTaskRunDetail {
    id: number;
    task_type: JobTaskType;
    status: JobTaskStatus;
    retry_count: number;
    max_retries: number;
    error_message: string | null;
    queued_at: string | null;
    processing_at: string | null;
    completed_at: string | null;
    failed_at: string | null;
    created_at: string;
    metadata_: IJobTaskMetadata | null;
}

export interface IJobRunDetail {
    id: number;
    status: JobStatus;
    current_phase: string | null;
    error_message: string | null;
    pending_at: string | null;
    processing_at: string | null;
    completed_at: string | null;
    failed_at: string | null;
    created_at: string;
    tasks: IJobTaskRunDetail[];
}

export interface IScopeJobRunInfo {
    id: number;
    document_title: string;
    status: string;
    user: IUserMinimal;
    organization: IOrganizationMinimal;
    clause_checklist_name: string;
    clause_checklist_id: number;
    created_at: string;
    processing_completed_at: string | null;
}

export interface IAdminScopeJobRun {
    scope: IScopeJobRunInfo;
    job: IJobRunDetail | null;
}
